import { Button } from '../../components/ui';
import {
  formatScore,
  isWeekly,
  plainLabel,
  type JourneyStage,
  type ScoreSection,
} from './scoreboard';
import {
  adRows,
  entryCounts,
  entryStatus,
  headline,
  momentum,
  momentumText,
  standouts,
} from './scoreboardDashboard';

export function ScoreboardOverview({
  sections,
  yesterday,
  onEntry,
}: {
  sections: ScoreSection[];
  yesterday: boolean;
  onEntry: (section: ScoreSection) => void;
}) {
  const entered = sections.filter((s) => entryStatus(s) === 'Entered').length;
  const waiting = sections.filter((s) => entryStatus(s) !== 'Entered').map((s) => s.name);
  const insights = standouts(sections);
  const stages: [JourneyStage, string][] = [
    ['reached', 'Reached'],
    ['engaged', 'Engaged'],
    ['hand', 'Raised a hand'],
    ['call', 'Call booked'],
  ];
  return (
    <>
      <section className="score-card score-summary" aria-label="Entry summary">
        <strong>
          {entered} of {sections.length} sections entered for{' '}
          {yesterday ? 'yesterday' : 'the selected day'}
        </strong>
        <p className="score-muted">
          {waiting.length
            ? `Waiting on: ${waiting.join(', ')}`
            : 'All sections entered. Thank you.'}
        </p>
      </section>
      <div className="score-tiles">
        {sections.map((section) => {
          const top = headline(section);
          const status = entryStatus(section);
          const counts = entryCounts(section);
          // A weekly headline is entered once a week, so the tile shows its last 7 days.
          const weekly = top ? isWeekly(top.label) : false;
          const value = top ? (weekly ? top.totals.seven : top.totals.selected)?.value : null;
          return (
            <section
              className="score-card score-tile"
              key={section.id}
              aria-label={`${section.name} summary`}
            >
              <h3 className="score-section-title">{section.name}</h3>
              <div className="score-headline-line">
                {value == null ? (
                  <span className="score-headline-empty">
                    {top ? 'Not entered' : 'No fields yet'}
                  </span>
                ) : (
                  <strong className="score-headline">{formatScore(value, top!)}</strong>
                )}
                <span className="score-headline-label">
                  {top ? plainLabel(top.label) : null}
                  {weekly ? ' · last 7 days' : ''}
                </span>
              </div>
              <span className="score-status">
                {status} · {counts.entered} of {counts.total}{' '}
                {counts.total !== section.total_fields ? 'daily ' : ''}fields
              </span>
              {section.can_edit ? (
                <Button
                  variant={status === 'Entered' ? 'secondary' : 'primary'}
                  onClick={() => onEntry(section)}
                >
                  {status === 'Entered' ? 'Update numbers' : 'Enter numbers'}
                  <span className="ui-visually-hidden">: {section.name}</span>
                </Button>
              ) : (
                <small>Read only</small>
              )}
            </section>
          );
        })}
      </div>
      <section className="score-card score-summary" aria-label="What stands out">
        <h3>What stands out</h3>
        {insights.length ? (
          <ul className="score-insights">
            {insights.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        ) : (
          <p>Nothing to compare yet.</p>
        )}
      </section>
      <section className="score-card" aria-label="The journey, last 7 days">
        <header className="score-card-head">
          <h3>The journey, last 7 days</h3>
        </header>
        <div className="score-table-scroll" role="region" aria-label="Journey numbers" tabIndex={0}>
          <table className="score-table score-journey">
            <thead>
              <tr>
                <th scope="col">Section</th>
                {stages.map(([key, title]) => (
                  <th scope="col" key={key}>
                    {title}
                  </th>
                ))}
                <th scope="col">Momentum</th>
              </tr>
            </thead>
            <tbody>
              {['linkedin-ads', 'linkedin-outbound', 'vsl-site', 'manychat'].flatMap((slug) => {
                const section = sections.find((s) => s.slug === slug);
                if (!section) return [];
                return [
                  <tr key={section.id}>
                    <th scope="row">{section.name}</th>
                    {stages.map(([key], index) => {
                      const cell = section.journey?.[key];
                      const before = stages[index - 1]?.[1].toLowerCase();
                      if (!cell?.mapped_fields)
                        return (
                          <td key={key} className="score-muted">
                            <span aria-hidden="true">—</span>
                            <span className="ui-visually-hidden">Not tracked</span>
                          </td>
                        );
                      const ratio =
                        index > 0 && cell.ratio != null
                          ? `${(cell.ratio * 100).toFixed(1)}%`
                          : null;
                      return (
                        <td
                          key={key}
                          title={
                            index > 0
                              ? `${ratio ? `${ratio} of ${before}` : 'No paired comparison'} · ${cell.paired_days}/7 paired days`
                              : undefined
                          }
                        >
                          {cell.value == null ? (
                            <span className="score-muted">
                              <span aria-hidden="true">—</span>
                              <span className="ui-visually-hidden">Not entered</span>
                            </span>
                          ) : (
                            formatScore(cell.value, { value_type: 'count', rate_format: null })
                          )}
                          {ratio ? (
                            <span className="score-journey-rate">
                              {ratio}
                              <span className="ui-visually-hidden"> of {before}</span>
                            </span>
                          ) : null}
                        </td>
                      );
                    })}
                    <td>
                      {momentum(headline(section)) === null ? (
                        <span className="score-muted">
                          <span aria-hidden="true">—</span>
                          <span className="ui-visually-hidden">Not enough comparable days yet</span>
                        </span>
                      ) : (
                        momentumText(headline(section)).replace(' vs previous 7 days', '')
                      )}
                    </td>
                  </tr>,
                ];
              })}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

export function ByAd({ section }: { section: ScoreSection }) {
  const rows = adRows(section);
  return (
    <section className="score-by-ad" aria-label="By ad">
      <h3>By ad · last 7 days</h3>
      {!rows.length ? (
        <p className="score-muted">
          No per-ad fields yet. Add fields named Ad name · metric in Edit fields.
        </p>
      ) : (
        <div className="score-table-scroll" role="region" aria-label="Ad numbers" tabIndex={0}>
          <table className="score-table">
            <thead>
              <tr>
                {['Ad', 'Spend', 'Ad views', 'Link clicks', 'Cost per click'].map((label) => (
                  <th scope="col" key={label}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.name}>
                  <th scope="row">{row.name}</th>
                  {(['spend', 'views', 'clicks', 'cpc'] as const).map((key) => (
                    <td key={key}>
                      {row[key]
                        ? formatScore(row[key].totals.seven?.value, row[key])
                        : 'Not tracked'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
